/**
 * CSV parsing and V1 tab recognition for the Sheets importer (spec section 8).
 *
 * V1 tab headers come from sheetIO.js HEADERS, so a file is recognised by
 * its header row regardless of its file name. Dates arrive as the sheet
 * displayed them: ISO (yyyy-mm-dd) or US (m/d/yyyy); both are accepted.
 */

/** RFC 4180 parser: quotes, escaped quotes, commas and newlines inside quotes. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') inQuotes = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else field += c;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((v) => v.trim() !== ''));
}

export type V1Tab =
  | 'AircraftTypes'
  | 'Aircraft'
  | 'Pilots'
  | 'Qualifications'
  | 'Unavailability'
  | 'Overrides'
  | 'Transitions'
  | 'AircraftPairing'
  | 'AlternatingPilots'
  | 'PreviousRotation'
  | 'Config'
  | 'CarryInState'
  | 'Schedule'
  | 'ScheduleDraft'
  | 'AmendmentLog'
  | 'PilotUtilization';

/** V1 headers (sheetIO.js). Recognition needs the leading required columns only. */
export const V1_HEADERS: Record<V1Tab, string[]> = {
  AircraftTypes: ['aircraft_type', 'swap_day'],
  Aircraft: ['tail_number', 'aircraft_type', 'active'],
  Pilots: ['pilot_id', 'last_name', 'first_name', 'start_date', 'termination_date', 'active', 'notes'],
  Qualifications: ['pilot_id', 'pilot_name', 'aircraft_type', 'seat', 'is_training_captain', 'supervised_rotations_remaining', 'active', 'active_start_date', 'active_end_date'],
  Unavailability: ['pilot_id', 'pilot_name', 'start_date', 'end_date', 'reason'],
  Overrides: ['type', 'pilot_id', 'pilot_name', 'tail_number', 'rotation_start_date', 'seat', 'days', 'notes'],
  Transitions: ['pilot_id', 'pilot_name', 'change_type', 'effective_date', 'from_aircraft_type', 'from_seat', 'to_aircraft_type', 'to_seat', 'notes'],
  AircraftPairing: ['pilot_id', 'pilot_name', 'tail_number', 'start_date', 'end_date', 'notes'],
  AlternatingPilots: ['pilot_id_1', 'pilot_name_1', 'pilot_id_2', 'pilot_name_2', 'start_date', 'end_date', 'notes'],
  PreviousRotation: ['tail_number', 'pic_pilot_id', 'pic_name', 'sic_pilot_id', 'sic_name', 'rotation_start_date', 'notes'],
  Config: ['key', 'value'],
  CarryInState: ['pilot_id', 'prev_rotation_start', 'prev_rotation_tail', 'prev_rotation_partner', 'last_rotation_start', 'last_rotation_tail', 'last_rotation_partner', 'phase_offset', 'supervised_remaining_json', 'fairness_deficit_json'],
  Schedule: ['rotation_start', 'rotation_end', 'tail', 'aircraft_type', 'PIC', 'PIC_name', 'SIC', 'SIC_name', 'flags'],
  // Same layout as Schedule; told apart by the sheet or file name. Never imported (a draft is regenerated, not migrated).
  ScheduleDraft: ['rotation_start', 'rotation_end', 'tail', 'aircraft_type', 'PIC', 'PIC_name', 'SIC', 'SIC_name', 'flags'],
  AmendmentLog: ['timestamp', 'user', 'tail', 'rotation_start', 'field', 'old_value', 'new_value', 'note'],
  // The Pilot Utilization tab has no header row; it is recognised by its A1 title.
  PilotUtilization: ['DRAFT UTILIZATION (auto)'],
};

/** Import order (spec section 8 step 2). */
export const IMPORT_ORDER: V1Tab[] = [
  'AircraftTypes', 'Aircraft', 'Pilots', 'Qualifications', 'Unavailability', 'Transitions',
  'AircraftPairing', 'AlternatingPilots', 'PreviousRotation', 'Overrides', 'Config', 'CarryInState', 'Schedule',
  'AmendmentLog', 'PilotUtilization',
];

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

/** Which V1 tab a header row belongs to, or null. Header order must match; extra trailing columns are fine. */
export function detectTab(header: string[], name = ''): V1Tab | null {
  const h = header.map(norm);
  if (h[0]?.startsWith('draft utilization')) return 'PilotUtilization';
  // Longest signatures first so Pilots (7 cols) is not mistaken for a shorter one.
  const tabs = (Object.keys(V1_HEADERS) as V1Tab[]).filter((t) => t !== 'ScheduleDraft').sort((a, b) => V1_HEADERS[b].length - V1_HEADERS[a].length);
  for (const tab of tabs) {
    const sig = V1_HEADERS[tab].map(norm);
    if (sig.length <= h.length && sig.every((c, i) => h[i] === c)) {
      // ScheduleDraft shares the Schedule header; the sheet or file name says which it is.
      if (tab === 'Schedule' && /draft/i.test(name)) return 'ScheduleDraft';
      return tab;
    }
  }
  return null;
}

/** Tabs that are recognised so the user sees them accounted for, but never written. */
export const NOT_IMPORTED: ReadonlySet<V1Tab> = new Set<V1Tab>(['ScheduleDraft']);

export interface ParsedFile {
  name: string;
  tab: V1Tab | null;
  header: string[];
  /** Data rows as objects keyed by the V1 header names (lower-cased). */
  rows: Record<string, string>[];
  /** The raw grid, kept for the positional Pilot Utilization layout. */
  grid?: string[][];
  /** True when this came from a sheet inside an uploaded workbook (unknown sheets are then expected, not errors). */
  fromWorkbook?: boolean;
  error?: string;
}

export function parseV1File(name: string, text: string): ParsedFile {
  const grid = parseCsv(text);
  const header = grid[0] ?? [];
  const tab = detectTab(header, name);
  if (!tab) {
    return { name, tab: null, header, rows: [], error: 'Header does not match any V1 tab' };
  }
  if (tab === 'PilotUtilization') {
    // Positional layout (D35): manual log from column D (index 3) onward.
    const rows = grid.slice(4).filter((r) => (r[3] ?? '').trim() !== '').map((r) => ({ pilot: (r[3] ?? '').trim() }));
    return { name, tab, header, rows, grid };
  }
  const keys = header.map(norm);
  const rows = grid.slice(1).map((r) => {
    const o: Record<string, string> = {};
    keys.forEach((k, i) => {
      o[k] = (r[i] ?? '').trim();
    });
    return o;
  });
  return { name, tab, header, rows };
}

/** 'yyyy-mm-dd' from ISO or US m/d/yyyy input; null for blank; the raw text when unparseable (validation reports it). */
export function toIsoDate(s: string | undefined | null): string | null {
  if (s === undefined || s === null) return null;
  const t = s.trim();
  if (!t) return null;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(t);
  if (iso) return `${iso[1]}-${iso[2]!.padStart(2, '0')}-${iso[3]!.padStart(2, '0')}`;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(t);
  if (us) {
    const y = us[3]!.length === 2 ? '20' + us[3] : us[3]!;
    return `${y}-${us[1]!.padStart(2, '0')}-${us[2]!.padStart(2, '0')}`;
  }
  return t;
}

/** V1 Y/N cells: blank means the caller's default. */
export function yn(s: string | undefined, blankIs: boolean): boolean {
  const t = (s ?? '').trim().toLowerCase();
  if (!t) return blankIs;
  return t === 'y' || t === 'yes' || t === 'true' || t === '1';
}

export function num(s: string | undefined): number | null {
  const t = (s ?? '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export function weekdayToIndex(name: string): number | null {
  const i = WEEKDAYS.indexOf(name.trim().toLowerCase());
  return i >= 0 ? i : null;
}

/** V1 Schedule flags column, e.g. "PIC: pinned+double; SIC: supervised; PIC-days: 12 (2026-08-05 – 2026-08-16)". */
export interface ParsedSeatFlags {
  pinned: boolean;
  double: boolean;
  supervised: boolean;
  actingTC: boolean;
  absorbed: boolean;
  days: number | null;
  start: string | null;
  end: string | null;
}
export function parseSeatFlags(flags: string, seat: 'PIC' | 'SIC'): ParsedSeatFlags {
  const out: ParsedSeatFlags = { pinned: false, double: false, supervised: false, actingTC: false, absorbed: false, days: null, start: null, end: null };
  const m = new RegExp(seat + ': ([a-z+]+)').exec(flags);
  if (m) {
    const set = m[1]!.split('+');
    out.pinned = set.includes('pinned');
    out.double = set.includes('double');
    out.supervised = set.includes('supervised');
    out.actingTC = set.includes('acting');
  }
  if (new RegExp(seat + ': no rotation').test(flags)) out.absorbed = true;
  const d = new RegExp(seat + '-days: (\\d+) \\(([0-9-]+) [–-] ([0-9-]+)\\)').exec(flags);
  if (d) {
    out.days = Number(d[1]);
    out.start = d[2] ?? null;
    out.end = d[3] ?? null;
  }
  return out;
}
